import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router';

import { PageHeader } from '@opengov/components-page-header';
import { ArrowLeft, ChevronDown, ChevronRight, Printer } from '@opengov/react-capital-assets';

import { CaseCard } from '../components/vcm/CaseCard';
import { CaseStatusBadge } from '../components/vcm/CaseStatusBadge';
import { SectionCard } from '../components/vcm/SectionCard';
import { TierBadge } from '../components/vcm/TierBadge';
import { useLanguage } from '../contexts/LanguageContext';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { formatDob } from '../lib/vcm/computed';
/* MOCK DATA — replace with real /v2 API calls when backend is connected */
import {
  mockGetClientById,
  mockGetCasesByClientId,
  mockGetHouseholdByClientId,
  mockGetCallLogByClientId,
} from '../mocks/vcm-mock-data';
import type { StructuredAddress } from '../types/vcm';

/** Boolean client flags surfaced as chips on the record header area. */
type ClientFlagKey =
  | 'headOfHousehold65OrOlder'
  | 'accessOrFunctionalNeeds'
  | 'unsafeUnsanitaryHousing'
  | 'persistentSocialServiceClient'
  | 'significantEmotionalDistress'
  | 'singleHeadWithMinors';

const FLAG_DEFS: { key: ClientFlagKey; label: string }[] = [
  { key: 'headOfHousehold65OrOlder', label: 'Head of Household 65+' },
  { key: 'accessOrFunctionalNeeds', label: 'Access / Functional Needs' },
  { key: 'unsafeUnsanitaryHousing', label: 'Unsafe / Unsanitary Housing' },
  { key: 'persistentSocialServiceClient', label: 'Persistent Social Service Client' },
  { key: 'significantEmotionalDistress', label: 'Significant Emotional Distress' },
  { key: 'singleHeadWithMinors', label: 'Single Head w/ Minors' },
];

function formatAddress(addr: StructuredAddress | null): string {
  if (!addr) return '—';
  const parts = [addr.address1, addr.address2, addr.city, addr.state, addr.zip].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '—';
}

function getInitials(first: string, last: string): string {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

export function ClientRecordPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const { navigateToPath } = useAppNavigation();
  const { t } = useLanguage();

  const client = clientId ? mockGetClientById(clientId) : null;
  const cases = clientId ? mockGetCasesByClientId(clientId) : [];
  const household = clientId ? mockGetHouseholdByClientId(clientId) : [];
  const callHistory = clientId ? mockGetCallLogByClientId(clientId) : [];
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);

  const eventCount = new Set(cases.map((c) => c.event)).size;

  // #13 — current address pulls from the most-recently-updated case (open first),
  // using its "current residence" choice to pick the address.
  const latestCase =
    [...cases]
      .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
      .find((c) => c.status !== 'Closed') ??
    [...cases].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))[0];
  const currentAddress = (() => {
    if (!client) return '—';
    const residence = (latestCase?.currentResidence ?? '').toLowerCase();
    if (residence.includes('same')) return formatAddress(client.primaryAddress);
    return formatAddress(client.mailingAddress ?? client.primaryAddress);
  })();

  if (!client) {
    return (
      <Box sx={{ p: (theme) => theme.spacing(3) }}>
        <PageHeader title="Client Record" />
        <SectionCard title="Not found">
          <Typography variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
            No client found for ID “{clientId}”.
          </Typography>
        </SectionCard>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: (theme) => theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: (theme) => theme.spacing(2.5),
      }}
    >
      <PageHeader
        title={`${client.firstName} ${client.lastName}`}
        description={client.id}
        actions={[
          <Chip
            key="event-count"
            label={`${eventCount} event${eventCount !== 1 ? 's' : ''}`}
            size="small"
            color="primary"
            variant="outlined"
          />,
          <Button
            key="back"
            variant="outlined"
            startIcon={<ArrowLeft />}
            onClick={() => navigateToPath('clients/search')}
          >
            {t('backToSearch')}
          </Button>,
          <Button
            key="print"
            variant="outlined"
            startIcon={<Printer />}
            onClick={() => window.print()}
          >
            {t('print')}
          </Button>,
        ]}
      />

      {/* ── Unified client header card ── */}
      <Card sx={{ overflow: 'hidden' }}>
        {/* Gradient banner */}
        <Box sx={{ px: 2.5, py: 2, bgcolor: 'grey.100', borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar sx={{ width: 44, height: 44, bgcolor: 'primary.main', color: 'white', fontWeight: 700 }}>
                {getInitials(client.firstName, client.lastName)}
              </Avatar>
              <Box>
                <Typography variant="overline" sx={{ color: (th) => th.palette.text.secondary, fontSize: 10 }}>{t('clientRecord')} &middot; {client.id}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{client.firstName} {client.lastName}</Typography>
                <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                  {client.county} County &middot; {client.organization}
                  {eventCount > 0 && <Chip label={`${eventCount} events`} size="small" color="primary" variant="outlined" sx={{ ml: 1, fontSize: 10, height: 20 }} />}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {client.militaryBranch && (
                <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, borderRadius: 1, bgcolor: (t) => t.palette.primary.light, border: 1, borderColor: (t) => t.palette.primary.main }}>
                  <Typography variant="overline" sx={{ fontSize: 9, color: (t) => t.palette.primary.dark, display: 'block', lineHeight: 1.2 }}>
                    {client.militaryBranch}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: (t) => t.palette.primary.dark, fontSize: 11 }}>
                    {client.militaryRank ?? 'Veteran'}
                  </Typography>
                </Box>
              )}
              <TierBadge tier={client.tier} />
              <CaseStatusBadge status={client.status} />
            </Stack>
          </Stack>
        </Box>

        {/* Info grid row 1 */}
        <Grid container sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Grid size={{ xs: 6, md: 3 }} sx={{ p: 1.5, borderRight: 1, borderColor: 'divider' }}>
            <Typography variant="overline" sx={{ fontSize: 10, color: (th) => th.palette.text.secondary }}>{t('dateOfBirth')}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDob(client.dateOfBirth)}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }} sx={{ p: 1.5, borderRight: { md: 1 }, borderColor: 'divider' }}>
            <Typography variant="overline" sx={{ fontSize: 10, color: (th) => th.palette.text.secondary }}>{t('currentAddress')}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{currentAddress}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }} sx={{ p: 1.5, borderRight: 1, borderColor: 'divider' }}>
            <Typography variant="overline" sx={{ fontSize: 10, color: (th) => th.palette.text.secondary }}>{t('managingOrganization')}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{client.organization}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }} sx={{ p: 1.5 }}>
            <Typography variant="overline" sx={{ fontSize: 10, color: (th) => th.palette.text.secondary }}>{t('openCases')}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{client.openCases} case{client.openCases > 1 ? 's' : ''}</Typography>
          </Grid>
        </Grid>

        {/* Info grid row 2 */}
        <Grid container sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Grid size={{ xs: 6, md: 3 }} sx={{ p: 1.5, borderRight: 1, borderColor: 'divider' }}>
            <Typography variant="overline" sx={{ fontSize: 10, color: (th) => th.palette.text.secondary }}>{t('phone')}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{client.phoneHome ?? '—'}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }} sx={{ p: 1.5, borderRight: { md: 1 }, borderColor: 'divider' }}>
            <Typography variant="overline" sx={{ fontSize: 10, color: (th) => th.palette.text.secondary }}>{t('email')}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{client.email ?? '—'}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }} sx={{ p: 1.5, borderRight: 1, borderColor: 'divider' }}>
            <Typography variant="overline" sx={{ fontSize: 10, color: (th) => th.palette.text.secondary }}>{t('preferredLanguage')}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{client.preferredLanguage ?? 'English'}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }} sx={{ p: 1.5 }}>
            <Typography variant="overline" sx={{ fontSize: 10, color: (th) => th.palette.text.secondary }}>{t('bestTimeToContact')}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Weekdays after 4:00 PM</Typography>
          </Grid>
        </Grid>

        {/* Flags row */}
        <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography variant="overline" sx={{ fontSize: 10, color: (th) => th.palette.text.secondary, mr: 0.5 }}>{t('flags')}</Typography>
            {FLAG_DEFS.map((flag) => {
              const active = client[flag.key];
              return (
                <Chip
                  key={flag.key}
                  label={flag.label}
                  size="small"
                  color={active ? 'success' : 'default'}
                  variant={active ? 'filled' : 'outlined'}
                  sx={active ? undefined : { color: (t) => t.palette.text.disabled }}
                />
              );
            })}
          </Stack>
        </Box>
      </Card>

      {/* ── Cases ── */}
      <SectionCard title={`${t('disasterCases')} (${cases.length})`}>
        {cases.length === 0 ? (
          <Typography variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
            {t('noCasesOnRecord')}
          </Typography>
        ) : (
          <Box>
            {cases.map((tcrCase) => (
              <CaseCard
                key={tcrCase.caseId}
                caseId={tcrCase.caseId}
                event={tcrCase.program ?? ''}
                status={tcrCase.status}
                programCode={tcrCase.programCode ?? ''}
                progression={tcrCase.progression}
                tier={tcrCase.tier}
                manager={tcrCase.assignedManager}
                organization={tcrCase.organization}
                onManage={() => navigateToPath(`cases/${tcrCase.caseId}`)}
              />
            ))}
          </Box>
        )}
      </SectionCard>

      {/* ── Call History ── */}
      {callHistory.length > 0 && (
        <SectionCard title={`${t('callHistory')} (${callHistory.length})`} disableContentPadding>
          <TableContainer sx={{ maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ width: '3%' }} />
                  <TableCell align="center">{t('date')}</TableCell>
                  <TableCell align="center">{t('case_')}</TableCell>
                  <TableCell align="center">{t('reason')}</TableCell>
                  <TableCell align="center">{t('notes')}</TableCell>
                  <TableCell align="center">{t('handledBy')}</TableCell>
                  <TableCell align="center">{t('outcome')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {callHistory.map((entry) => {
                  const isExpanded = expandedCallId === entry.id;
                  return (
                    <>
                      <TableRow
                        key={entry.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setExpandedCallId(isExpanded ? null : entry.id)}
                      >
                        <TableCell align="center" sx={{ px: 0.5, color: 'text.secondary', '& svg': { width: 16, height: 16 } }}>
                          {isExpanded ? <ChevronDown /> : <ChevronRight />}
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>{new Date(entry.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell align="center">
                          <Typography variant="caption">{entry.caseName ?? '—'}</Typography>
                        </TableCell>
                        <TableCell align="center">{entry.reasonForCall}</TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {entry.notes || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{entry.handledBy}</TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{entry.outcome}</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow key={`${entry.id}-detail`}>
                        <TableCell colSpan={7} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1.5, px: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: (theme) => theme.palette.text.secondary }}>
                                {t('fullNotes')} — {new Date(entry.createdAt).toLocaleDateString()} · {entry.handledBy}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                {entry.notes || 'No notes recorded.'}
                              </Typography>
                              {entry.referralName && (
                                <Typography variant="caption" sx={{ mt: 1, display: 'block', color: (theme) => theme.palette.text.secondary }}>
                                  Referral: {entry.referralName}
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
      )}

      {/* ── Household ── */}
      <SectionCard title={`${t('householdMembers')} (${household.length})`} disableContentPadding>
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="center">{t('name')}</TableCell>
                <TableCell align="center">{t('relationship')}</TableCell>
                <TableCell align="center">{t('dateOfBirth')}</TableCell>
                <TableCell align="center">{t('condition')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {household.map((member) => (
                <TableRow key={member.id} hover>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {member.firstName} {member.lastName}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{member.relationship}</TableCell>
                  <TableCell align="center">{formatDob(member.dateOfBirth)}</TableCell>
                  <TableCell align="center">{member.condition}</TableCell>
                </TableRow>
              ))}
              {household.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: (theme) => theme.spacing(3) }}>
                    <Typography
                      variant="body2"
                      sx={{ color: (theme) => theme.palette.text.secondary }}
                    >
                      {t('noHouseholdMembers')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>
    </Box>
  );
}

function _DetailRow({ label, value }: { readonly label: string; readonly value: ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, color: (theme) => theme.palette.text.secondary }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  );
}
